import { useNavigate } from "react-router-dom";
import type { PageBlock, BlockType } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import { BLOCK_TYPES, InsertBar } from "./shared";

interface BlockRendererProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onUpdate: (b: PageBlock) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export default function BlockRenderer({
  block, isAdmin, isDark, headingFont, headingStyle,
  onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast,
}: BlockRendererProps) {
  const navigate = useNavigate();
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  if (block.type === "section") return null; // Handled by SectionBlockRenderer

  const btnBase = "flex items-center gap-1 px-2.5 py-1 rounded-md cursor-pointer text-xs font-bold whitespace-nowrap transition-colors";
  const btnMove = isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-gray-200" : "bg-white text-gray-500 hover:bg-gray-100 border border-gray-200";
  const btnDelete = "bg-red-500 text-white hover:bg-red-600";

  const AdminControls = () => (
    <div className={`flex items-center justify-between mb-2 rounded-lg px-2 py-1.5 ${isDark ? "bg-gray-800/80 border border-gray-700" : "bg-gray-50 border border-gray-200"}`}>
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${subColor}`}>{BLOCK_TYPES.find((b) => b.type === block.type)?.label}</span>
        <span className={`text-[10px] ${subColor}`}>·</span>
        <span className="text-[10px] text-gray-400 font-mono">{block.id.slice(-6)}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={onMoveUp} disabled={isFirst} className={`${btnBase} ${btnMove} ${isFirst ? "opacity-30 cursor-not-allowed" : ""}`}><i className="ri-arrow-up-line text-xs"></i> Up</button>
        <button onClick={onMoveDown} disabled={isLast} className={`${btnBase} ${btnMove} ${isLast ? "opacity-30 cursor-not-allowed" : ""}`}><i className="ri-arrow-down-line text-xs"></i> Down</button>
        <button onClick={onDelete} className={`${btnBase} ${btnDelete}`}><i className="ri-delete-bin-line text-xs"></i> Delete</button>
      </div>
    </div>
  );

  const levelClass: Record<string, string> = {
    h1: "text-4xl md:text-5xl font-extrabold",
    h2: "text-3xl md:text-4xl font-extrabold",
    h3: "text-2xl md:text-3xl font-bold",
    h4: "text-xl md:text-2xl font-bold",
  };

  const textStyleClass = (style?: string) => {
    switch (style) {
      case "card": return isDark ? "rounded-2xl p-6 bg-gray-800 border border-gray-700" : "rounded-2xl p-6 bg-white border border-gray-100 shadow-sm";
      case "gradient": return isDark ? "rounded-2xl p-6 bg-gray-800" : "rounded-2xl p-6";
      default: return "";
    }
  };

  const textStyleBg = (style?: string) => {
    if (style === "gradient") return isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" };
    return {};
  };

  switch (block.type) {
    case "heading": {
      const level = block.headingLevel ?? "h2";
      const Tag = level as "h1" | "h2" | "h3" | "h4";
      return (
        <div className={`mb-4 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          {isAdmin && (
            <div className="flex items-center gap-1.5 mb-2 ml-1">
              <span className={`text-[10px] font-bold ${subColor}`}>Size:</span>
              {(["h1", "h2", "h3", "h4"] as const).map((l) => (
                <button key={l} onClick={() => onUpdate({ ...block, headingLevel: l })} className={`text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${level === l ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          {isAdmin ? (
            <AdminEditField value={block.content} onSave={(v) => onUpdate({ ...block, content: v })} inputClassName={levelClass[level]}>
              <Tag className={`${levelClass[level]} ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.content}</Tag>
            </AdminEditField>
          ) : (
            <Tag className={`${levelClass[level]} mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.content}</Tag>
          )}
        </div>
      );
    }

    case "text": {
      const style = block.textStyle ?? "default";
      return (
        <div className={`mb-6 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          {isAdmin && (
            <div className="flex items-center gap-1.5 mb-2 ml-1">
              <span className={`text-[10px] font-bold ${subColor}`}>Style:</span>
              {(["default", "card", "gradient"] as const).map((s) => (
                <button key={s} onClick={() => onUpdate({ ...block, textStyle: s })} className={`text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors capitalize ${style === s ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{s}</button>
              ))}
            </div>
          )}
          <div className={textStyleClass(style)} style={textStyleBg(style)}>
            {isAdmin ? (
              <AdminEditField value={block.content} onSave={(v) => onUpdate({ ...block, content: v })} multiline richText inputClassName="text-base leading-relaxed">
                <div className={`text-base leading-relaxed ${subColor}`} dangerouslySetInnerHTML={{ __html: block.content }} />
              </AdminEditField>
            ) : (
              <div className={`text-base leading-relaxed ${subColor}`} dangerouslySetInnerHTML={{ __html: block.content }} />
            )}
          </div>
        </div>
      );
    }

    case "image": {
      const imgSize = block.imageSize ?? "full";
      const showCaption = block.showCaption !== false;
      const sizeWrapClass: Record<string, string> = { full: "w-full", large: "w-full max-w-2xl mx-auto", medium: "w-full max-w-lg mx-auto", small: "w-full max-w-xs mx-auto" };
      const heightClass: Record<string, string> = { full: "max-h-[500px]", large: "max-h-[400px]", medium: "max-h-[300px]", small: "max-h-[200px]" };
      return (
        <div className={`mb-8 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          {isAdmin && (
            <div className="flex items-center gap-2 mb-2 ml-1 flex-wrap">
              <span className={`text-xs font-bold ${subColor}`}>Size:</span>
              {(["full", "large", "medium", "small"] as const).map((sz) => (
                <button key={sz} onClick={() => onUpdate({ ...block, imageSize: sz })} className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors capitalize shadow-sm ${imgSize === sz ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{sz}</button>
              ))}
              <span className={`text-xs font-bold ml-3 ${subColor}`}>Caption:</span>
              <button onClick={() => onUpdate({ ...block, showCaption: !showCaption })} className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors shadow-sm ${showCaption ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{showCaption ? "On" : "Off"}</button>
            </div>
          )}
          <figure className={sizeWrapClass[imgSize]}>
            <div className="rounded-2xl overflow-hidden">
              {isAdmin ? (
                <AdminImageUpload src={block.content || ""} alt={block.imageAlt ?? ""} onReplace={(url) => onUpdate({ ...block, content: url })} objectPosition={block.desktopPosition ?? "50% 50%"} onPositionChange={(pos) => onUpdate({ ...block, desktopPosition: pos })} mobileObjectPosition={block.mobilePosition ?? block.desktopPosition ?? "50% 50%"} onMobilePositionChange={(pos) => onUpdate({ ...block, mobilePosition: pos })} className="w-full" imgClassName={`w-full ${heightClass[imgSize]} object-cover`} isDark={isDark} />
              ) : (
                block.content && <img src={block.content} alt={block.imageAlt ?? ""} className={`w-full ${heightClass[imgSize]} object-cover`} style={{ objectPosition: block.desktopPosition ?? "50% 50%" }} />
              )}
            </div>
            {showCaption && (
              isAdmin ? (
                <AdminEditField value={block.imageAlt ?? ""} onSave={(v) => onUpdate({ ...block, imageAlt: v })} inputClassName="text-sm text-center">
                  <figcaption className={`text-sm text-center mt-2 italic ${subColor}`}>{block.imageAlt || "Click to add caption..."}</figcaption>
                </AdminEditField>
              ) : (
                block.imageAlt && <figcaption className={`text-sm text-center mt-2 italic ${subColor}`}>{block.imageAlt}</figcaption>
              )
            )}
          </figure>
        </div>
      );
    }

    case "image-grid": {
      const images = block.images ?? [];
      const cols = block.imageCols ?? 2;
      const gridCols = cols === 3 ? "grid-cols-3" : cols === 4 ? "grid-cols-4" : "grid-cols-2";
      return (
        <div className={`mb-8 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          {isAdmin && (
            <div className="flex items-center gap-2 mb-2 ml-1 flex-wrap">
              <span className={`text-xs font-bold ${subColor}`}>Columns:</span>
              {([2, 3, 4] as const).map((c) => (
                <button key={c} onClick={() => onUpdate({ ...block, imageCols: c })} className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors shadow-sm ${cols === c ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{c} col{c > 2 ? "s" : ""}</button>
              ))}
              <button onClick={() => onUpdate({ ...block, images: [...images, { id: `img-${Date.now()}`, url: "", alt: "" }] })} className="text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap bg-green-100 text-green-700 hover:bg-green-200 transition-colors shadow-sm ml-auto">
                <i className="ri-add-line mr-0.5"></i>Add Image
              </button>
            </div>
          )}
          <div className={`grid ${gridCols} gap-4`}>
            {images.map((img, imgIdx) => (
              <div key={img.id} className={`rounded-xl overflow-hidden relative ${isAdmin ? "group/gridimg" : ""}`}>
                {isAdmin && (
                  <button onClick={() => onUpdate({ ...block, images: images.filter((_, i) => i !== imgIdx) })} className="absolute top-1.5 right-1.5 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 cursor-pointer shadow-sm transition-colors">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                )}
                {isAdmin ? (
                  <AdminImageUpload src={img.url || ""} alt={img.alt} onReplace={(url) => onUpdate({ ...block, images: images.map((im, i) => i === imgIdx ? { ...im, url } : im) })} objectPosition={img.desktopPosition ?? "50% 50%"} onPositionChange={(pos) => onUpdate({ ...block, images: images.map((im, i) => i === imgIdx ? { ...im, desktopPosition: pos } : im) })} mobileObjectPosition={img.mobilePosition ?? img.desktopPosition ?? "50% 50%"} onMobilePositionChange={(pos) => onUpdate({ ...block, images: images.map((im, i) => i === imgIdx ? { ...im, mobilePosition: pos } : im) })} className="w-full" imgClassName="w-full h-48 object-cover" isDark={isDark} />
                ) : (
                  img.url && <img src={img.url} alt={img.alt} className="w-full h-48 object-cover" style={{ objectPosition: img.desktopPosition ?? "50% 50%" }} />
                )}
                {isAdmin && (
                  <AdminEditField value={img.alt} onSave={(v) => onUpdate({ ...block, images: images.map((im, i) => i === imgIdx ? { ...im, alt: v } : im) })} inputClassName="text-xs text-center mt-1" className="mt-1">
                    <p className={`text-xs text-center mt-1 ${subColor}`}>{img.alt || "Caption..."}</p>
                  </AdminEditField>
                )}
                {!isAdmin && img.alt && <p className={`text-xs text-center mt-1 ${subColor}`}>{img.alt}</p>}
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "image-detail":
      return (
        <div className={`mb-8 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          <div className={`rounded-2xl overflow-hidden border ${isDark ? "border-gray-700 bg-gray-800" : "border-gray-100 bg-gray-50"}`}>
            <div className="w-full h-64 overflow-hidden">
              {isAdmin ? (
                <AdminImageUpload src={block.content || ""} alt={block.imageAlt ?? ""} onReplace={(url) => onUpdate({ ...block, content: url })} objectPosition={block.desktopPosition ?? "50% 50%"} onPositionChange={(pos) => onUpdate({ ...block, desktopPosition: pos })} mobileObjectPosition={block.mobilePosition ?? block.desktopPosition ?? "50% 50%"} onMobilePositionChange={(pos) => onUpdate({ ...block, mobilePosition: pos })} className="w-full h-full" imgClassName="w-full h-full object-cover" isDark={isDark} />
              ) : (
                block.content && <img src={block.content} alt={block.imageAlt ?? ""} className="w-full h-full object-cover" style={{ objectPosition: block.desktopPosition ?? "50% 50%" }} />
              )}
            </div>
            <div className="p-5">
              {isAdmin ? (
                <AdminEditField value={block.subContent ?? ""} onSave={(v) => onUpdate({ ...block, subContent: v })} inputClassName="font-bold text-lg">
                  <h3 className={`font-bold text-lg mb-2 ${headingColor}`}>{block.subContent}</h3>
                </AdminEditField>
              ) : (
                block.subContent && <h3 className={`font-bold text-lg mb-2 ${headingColor}`}>{block.subContent}</h3>
              )}
              {isAdmin ? (
                <AdminEditField value={block.ctaLabel ?? ""} onSave={(v) => onUpdate({ ...block, ctaLabel: v })} multiline inputClassName="text-sm leading-relaxed">
                  <p className={`text-sm leading-relaxed ${subColor}`}>{block.ctaLabel}</p>
                </AdminEditField>
              ) : (
                block.ctaLabel && <p className={`text-sm leading-relaxed ${subColor}`}>{block.ctaLabel}</p>
              )}
            </div>
          </div>
        </div>
      );

    case "two-col": {
      const leftType = block.leftType ?? "text";
      const rightType = block.rightType ?? "text";
      const leftLevel = block.leftHeadingLevel ?? "none";
      const rightLevel = block.rightHeadingLevel ?? "none";
      const leftLevelClass = levelClass[leftLevel] || "";
      const rightLevelClass = levelClass[rightLevel] || "";
      const LeftTag = leftLevel as "h1" | "h2" | "h3" | "h4";
      const RightTag = rightLevel as "h1" | "h2" | "h3" | "h4";
      return (
        <div className={`mb-8 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          {isAdmin && (
            <div className="flex items-center gap-2 mb-2 ml-1 flex-wrap">
              <span className={`text-xs font-bold ${subColor}`}>Left:</span>
              {(["text", "image"] as const).map((t) => <button key={t} onClick={() => onUpdate({ ...block, leftType: t })} className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${leftType === t ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{t}</button>)}
              <span className={`text-xs font-bold ml-3 ${subColor}`}>Right:</span>
              {(["text", "image"] as const).map((t) => <button key={t} onClick={() => onUpdate({ ...block, rightType: t })} className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${rightType === t ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{t}</button>)}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              {isAdmin && (
                <div className="flex items-center gap-1 mb-2">
                  <span className={`text-[10px] font-bold ${subColor}`}>Heading:</span>
                  {(["h2", "h3", "h4", "none"] as const).map((l) => {
                    const active = (l === "none" && !block.leftHeadingLevel) || block.leftHeadingLevel === l;
                    return <button key={l} onClick={() => onUpdate({ ...block, leftHeadingLevel: l === "none" ? undefined : l, leftHeading: l === "none" ? undefined : (block.leftHeading || "Column Heading") })} className={`text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${active ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{l.toUpperCase()}</button>;
                  })}
                </div>
              )}
              {block.leftHeading && block.leftHeadingLevel && block.leftHeadingLevel !== "none" && (
                <div className="mb-2">
                  {isAdmin ? <AdminEditField value={block.leftHeading} onSave={(v) => onUpdate({ ...block, leftHeading: v })} inputClassName={leftLevelClass}><LeftTag className={`${leftLevelClass} ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.leftHeading}</LeftTag></AdminEditField> : <LeftTag className={`${leftLevelClass} ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.leftHeading}</LeftTag>}
                </div>
              )}
              {leftType === "image" ? (
                <div className="rounded-xl overflow-hidden">
                  {isAdmin ? <AdminImageUpload src={block.leftImage || ""} alt={block.leftImageAlt ?? ""} onReplace={(url) => onUpdate({ ...block, leftImage: url })} className="w-full" imgClassName="w-full h-64 object-cover" isDark={isDark} /> : block.leftImage && <img src={block.leftImage} alt={block.leftImageAlt ?? ""} className="w-full h-64 object-cover" />}
                  {isAdmin && <AdminEditField value={block.leftImageAlt ?? ""} onSave={(v) => onUpdate({ ...block, leftImageAlt: v })} inputClassName="text-xs text-center mt-1"><p className={`text-xs text-center mt-1 ${subColor}`}>{block.leftImageAlt || "Caption..."}</p></AdminEditField>}
                </div>
              ) : (
                isAdmin ? <AdminEditField value={block.content} onSave={(v) => onUpdate({ ...block, content: v })} multiline richText inputClassName="text-base leading-relaxed"><div className={`text-base leading-relaxed ${subColor}`} dangerouslySetInnerHTML={{ __html: block.content }} /></AdminEditField> : <div className={`text-base leading-relaxed ${subColor}`} dangerouslySetInnerHTML={{ __html: block.content }} />
              )}
            </div>
            <div>
              {isAdmin && (
                <div className="flex items-center gap-1 mb-2">
                  <span className={`text-[10px] font-bold ${subColor}`}>Heading:</span>
                  {(["h2", "h3", "h4", "none"] as const).map((l) => {
                    const active = (l === "none" && !block.rightHeadingLevel) || block.rightHeadingLevel === l;
                    return <button key={l} onClick={() => onUpdate({ ...block, rightHeadingLevel: l === "none" ? undefined : l, rightHeading: l === "none" ? undefined : (block.rightHeading || "Column Heading") })} className={`text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${active ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{l.toUpperCase()}</button>;
                  })}
                </div>
              )}
              {block.rightHeading && block.rightHeadingLevel && block.rightHeadingLevel !== "none" && (
                <div className="mb-2">
                  {isAdmin ? <AdminEditField value={block.rightHeading} onSave={(v) => onUpdate({ ...block, rightHeading: v })} inputClassName={rightLevelClass}><RightTag className={`${rightLevelClass} ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.rightHeading}</RightTag></AdminEditField> : <RightTag className={`${rightLevelClass} ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.rightHeading}</RightTag>}
                </div>
              )}
              {rightType === "image" ? (
                <div className="rounded-xl overflow-hidden">
                  {isAdmin ? <AdminImageUpload src={block.rightImage || ""} alt={block.rightImageAlt ?? ""} onReplace={(url) => onUpdate({ ...block, rightImage: url })} className="w-full" imgClassName="w-full h-64 object-cover" isDark={isDark} /> : block.rightImage && <img src={block.rightImage} alt={block.rightImageAlt ?? ""} className="w-full h-64 object-cover" />}
                  {isAdmin && <AdminEditField value={block.rightImageAlt ?? ""} onSave={(v) => onUpdate({ ...block, rightImageAlt: v })} inputClassName="text-xs text-center mt-1"><p className={`text-xs text-center mt-1 ${subColor}`}>{block.rightImageAlt || "Caption..."}</p></AdminEditField>}
                </div>
              ) : (
                isAdmin ? <AdminEditField value={block.subContent ?? ""} onSave={(v) => onUpdate({ ...block, subContent: v })} multiline richText inputClassName="text-base leading-relaxed"><div className={`text-base leading-relaxed ${subColor}`} dangerouslySetInnerHTML={{ __html: block.subContent ?? "" }} /></AdminEditField> : <div className={`text-base leading-relaxed ${subColor}`} dangerouslySetInnerHTML={{ __html: block.subContent ?? "" }} />
              )}
            </div>
          </div>
        </div>
      );
    }

    case "cta":
      return (
        <div className={`mb-8 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30" : ""}`}>
          {isAdmin && <AdminControls />}
          <div className={`rounded-2xl p-10 text-center ${isDark ? "bg-gray-800" : "bg-pink-50"}`}>
            {isAdmin ? (
              <AdminEditField value={block.content} onSave={(v) => onUpdate({ ...block, content: v })} inputClassName="text-2xl font-extrabold text-center">
                <h3 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.content}</h3>
              </AdminEditField>
            ) : <h3 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{block.content}</h3>}
            {isAdmin ? <AdminEditField value={block.subContent ?? ""} onSave={(v) => onUpdate({ ...block, subContent: v })} multiline inputClassName="text-base text-center">{block.subContent && <p className={`text-base mb-6 ${subColor}`}>{block.subContent}</p>}</AdminEditField> : block.subContent && <p className={`text-base mb-6 ${subColor}`}>{block.subContent}</p>}
            {isAdmin ? (
              <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                <AdminEditField value={block.ctaLabel ?? ""} onSave={(v) => onUpdate({ ...block, ctaLabel: v })} inputClassName="font-bold text-center">
                  <button className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors">{block.ctaLabel || "Button Label"}</button>
                </AdminEditField>
                <AdminEditField value={block.ctaUrl ?? ""} onSave={(v) => onUpdate({ ...block, ctaUrl: v })} inputClassName="text-xs text-center">
                  <span className={`text-xs ${subColor}`}>URL: {block.ctaUrl || "/"}</span>
                </AdminEditField>
              </div>
            ) : block.ctaLabel && (
              <button onClick={() => {
                const url = block.ctaUrl ?? "/";
                if (url.startsWith("http")) {
                  window.open(url, "_blank", "noopener noreferrer");
                } else if (url.startsWith("#")) {
                  // Anchor scroll — if on home page try directly, else navigate home first
                  const el = document.querySelector(url);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth" });
                  } else {
                    navigate("/");
                    setTimeout(() => document.querySelector(url)?.scrollIntoView({ behavior: "smooth" }), 400);
                  }
                } else {
                  navigate(url);
                }
              }} className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors">
                {block.ctaLabel}
              </button>
            )}
          </div>
        </div>
      );

    case "divider":
      return (
        <div className={`my-10 ${isAdmin ? "rounded-xl p-2 border border-pink-200 bg-pink-50/30 min-h-[40px]" : ""}`}>
          {isAdmin && <AdminControls />}
          <hr className={`${isDark ? "border-gray-700" : "border-gray-200"}`} />
        </div>
      );

    default:
      return null;
  }
}

export { InsertBar, BLOCK_TYPES };